'use client';

/** One attachment card under a chat message. */

import React from 'react';
import type { MessageFilePart } from '../../utils/messageTimeline';

function ImageIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
    </svg>
  );
}

export function FileAttachment({ file }: { file: MessageFilePart }) {
  // `contentType` is dropped by AI SDK validation; `mediaType` survives — read
  // both so the icon is right on loaded messages.
  const mimeType = file.contentType ?? file.mediaType ?? '';
  return (
    <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-sm">
      <div className="text-gray-600 dark:text-gray-400 flex-shrink-0">
        {mimeType.startsWith('image/') ? <ImageIcon /> : <DocumentIcon />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
          {file.filename}
        </div>
        {file.size !== undefined && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {(file.size / 1024).toFixed(1)} KB
          </div>
        )}
      </div>
      {file.url && (
        <a
          href={file.url}
          download={file.filename}
          className="flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
          title="Download file"
        >
          <svg
            className="w-4 h-4 text-gray-600 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        </a>
      )}
    </div>
  );
}
